import { Fragment, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, History, Medal, Pencil, Plus, RefreshCw, Star, Trophy, X } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { PageTitle, Panel, StarDivider } from "@/components/common";
import { toast } from "sonner";

const EMPTY_FORM = {
  name: "",
  state: "draft",
  clan_ranking_mode: "placement",
  placement_points: {},
  game_win_bonus: 0,
  game_top3_bonus: 0,
  tournament_win_bonus: 0,
};

const STATE_LABELS = {
  draft: "Bozza",
  active: "Attivo",
  completed: "Completato",
  archived: "Archiviato",
};

const NEXT_STATES = {
  draft: ["draft", "active"],
  active: ["active", "completed"],
  completed: ["completed", "archived"],
  archived: ["archived"],
};

const MODE_LABELS = {
  placement: "Classifica piazzamenti",
  balanced: "Classifica bilanciata",
};

function RankIcon({ rank }) {
  if (rank === 1) {
    return <Trophy className="w-5 h-5 text-[#D4AF37]" />;
  }
  if (rank === 2) {
    return <Medal className="w-5 h-5 text-[#9CA3AF]" />;
  }
  if (rank === 3) {
    return <Medal className="w-5 h-5 text-[#997A1A]" />;
  }

  return (
    <span className="font-oswald font-bold text-[var(--silver)] w-5 text-center inline-block">
      {rank}
    </span>
  );
}

export default function Campionati() {
  const [championships, setChampionships] = useState([]);

  const activeChampionships = championships.filter(
    (c) => c.state === "draft" || c.state === "active"
  );
  const completedChampionships = championships.filter(
    (c) => c.state === "completed" || c.state === "archived"
  );
  const [expandedCompletedIds, setExpandedCompletedIds] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [rankings, setRankings] = useState({});
  const [histories, setHistories] = useState({});
  const [rankingLoading, setRankingLoading] = useState({});
  const [championshipAdjustments, setChampionshipAdjustments] = useState({});
  const [adjustmentForms, setAdjustmentForms] = useState({});
  const [adjustmentSaving, setAdjustmentSaving] = useState({});
  const [adjustmentTournamentTeams, setAdjustmentTournamentTeams] = useState({});
  const [adjustmentTeamsLoading, setAdjustmentTeamsLoading] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [
        { data: championshipRows },
        { data: tournamentRows },
      ] = await Promise.all([
        api.get("/championships"),
        api.get("/tournaments", {
          params: { templates: false },
        }),
      ]);
      setChampionships(championshipRows);
      setTournaments(tournamentRows);
    } catch (error) {
      toast.error(formatError(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const loadChampionshipRankings = async (championshipId) => {
    setRankingLoading((current) => ({
      ...current,
      [championshipId]: true,
    }));

    try {
      const [
        { data: clans },
        { data: mvp },
        { data: history },
        { data: adjustments },
      ] = await Promise.all([
        api.get(`/championships/${championshipId}/ranking/clans`),
        api.get(`/championships/${championshipId}/ranking/mvp`),
        api.get(`/championships/${championshipId}/history`),
        api.get("/championship-adjustments", {
          params: { championship_id: championshipId },
        }),
      ]);

      setRankings((current) => ({
        ...current,
        [championshipId]: { clans, mvp },
      }));

      setHistories((current) => ({
        ...current,
        [championshipId]: history,
      }));

      setChampionshipAdjustments((current) => ({
        ...current,
        [championshipId]: adjustments,
      }));
    } catch (error) {
      toast.error(formatError(error.response?.data?.detail));
    } finally {
      setRankingLoading((current) => ({
        ...current,
        [championshipId]: false,
      }));
    }
  };

  const toggleRankings = async (championshipId) => {
    if (expandedId === championshipId) {
      setExpandedId(null);
      return;
    }

    setExpandedId(championshipId);

    // Ricarica sempre quando si apre il Campionato:
    // classifiche e storico restano allineati ai dati sorgente correnti.
    await loadChampionshipRankings(championshipId);
  };

  const getAdjustmentForm = (championshipId) =>
    adjustmentForms[championshipId] || {
      tournament_id: "",
      clan_id: "",
      value: "",
      reason: "",
    };

  const updateAdjustmentForm = (championshipId, patch) => {
    setAdjustmentForms((current) => ({
      ...current,
      [championshipId]: {
        ...getAdjustmentForm(championshipId),
        ...patch,
      },
    }));
  };

  const adjustmentTeamsKey = (championshipId, tournamentId) =>
    `${championshipId}:${tournamentId}`;

  const loadAdjustmentTournamentTeams = async (
    championshipId,
    tournamentId
  ) => {
    if (!tournamentId) {
      return;
    }

    const key = adjustmentTeamsKey(championshipId, tournamentId);

    setAdjustmentTeamsLoading((current) => ({
      ...current,
      [key]: true,
    }));

    try {
      const { data } = await api.get("/teams", {
        params: { tournament_id: tournamentId },
      });

      setAdjustmentTournamentTeams((current) => ({
        ...current,
        [key]: Array.isArray(data) ? data : [],
      }));
    } catch (error) {
      setAdjustmentTournamentTeams((current) => ({
        ...current,
        [key]: [],
      }));

      toast.error(
        formatError(
          error.response?.data?.detail ||
            "Impossibile caricare i Clan partecipanti."
        )
      );
    } finally {
      setAdjustmentTeamsLoading((current) => ({
        ...current,
        [key]: false,
      }));
    }
  };

  const adjustmentClanOptions = (championshipId, tournamentId) => {
    if (!tournamentId) {
      return [];
    }

    const key = adjustmentTeamsKey(championshipId, tournamentId);
    const teams = adjustmentTournamentTeams[key] || [];

    const clanIds = new Set(
      teams
        .map((team) => team.clan_id)
        .filter(Boolean)
    );

    const options = new Map();

    for (const clan of clans) {
      if (!clanIds.has(clan.id)) {
        continue;
      }

      options.set(clan.id, {
        id: clan.id,
        name: clan.name || "Clan",
        tag: clan.tag || "",
      });
    }

    /*
     * Defensive fallback:
     * if a participating Team references a Clan that is not present in the
     * currently loaded Clan catalogue, keep it selectable by ID.
     * Backend validation remains authoritative.
     */
    for (const clanId of clanIds) {
      if (!options.has(clanId)) {
        options.set(clanId, {
          id: clanId,
          name: clanId,
          tag: "",
        });
      }
    }

    return Array.from(options.values()).sort((a, b) =>
      `${a.name} ${a.tag}`.localeCompare(
        `${b.name} ${b.tag}`,
        "it",
        { sensitivity: "base" }
      )
    );
  };

  const createChampionshipAdjustment = async (championshipId) => {
    const form = getAdjustmentForm(championshipId);

    const value = Number(form.value);
    const reason = form.reason.trim();

    if (!form.tournament_id) {
      toast.error("Seleziona un torneo.");
      return;
    }

    if (!form.clan_id) {
      toast.error("Seleziona un Clan.");
      return;
    }

    if (!Number.isFinite(value) || value === 0) {
      toast.error("Inserisci una variazione punti valida e diversa da zero.");
      return;
    }

    if (!reason) {
      toast.error("La motivazione è obbligatoria.");
      return;
    }

    setAdjustmentSaving((current) => ({
      ...current,
      [championshipId]: true,
    }));

    try {
      await api.post("/championship-adjustments", {
        championship_id: championshipId,
        tournament_id: form.tournament_id,
        clan_id: form.clan_id,
        value,
        reason,
      });

      toast.success("Adjustment amministrativo salvato.");

      setAdjustmentForms((current) => ({
        ...current,
        [championshipId]: {
          tournament_id: "",
          clan_id: "",
          value: "",
          reason: "",
        },
      }));

      await loadChampionshipRankings(championshipId);
    } catch (error) {
      toast.error(formatError(error.response?.data?.detail));
    } finally {
      setAdjustmentSaving((current) => ({
        ...current,
        [championshipId]: false,
      }));
    }
  };

  const deleteChampionshipAdjustment = async (
    championshipId,
    adjustment
  ) => {
    const confirmed = window.confirm(
      `Eliminare questo adjustment amministrativo?\n\n` +
        `${adjustment.tournament_name || "Torneo"} · ` +
        `${adjustment.clan_name || adjustment.clan_tag || "Clan"} · ` +
        `${Number(adjustment.value) > 0 ? "+" : ""}${adjustment.value} punti\n\n` +
        `La classifica Clan del Campionato verrà ricalcolata.`
    );

    if (!confirmed) {
      return;
    }

    setAdjustmentSaving((current) => ({
      ...current,
      [championshipId]: true,
    }));

    try {
      await api.delete(
        `/championship-adjustments/${adjustment.id}`
      );

      toast.success("Adjustment amministrativo eliminato.");
      await loadChampionshipRankings(championshipId);
    } catch (error) {
      toast.error(formatError(error.response?.data?.detail));
    } finally {
      setAdjustmentSaving((current) => ({
        ...current,
        [championshipId]: false,
      }));
    }
  };

  const tournamentsByChampionship = useMemo(() => {
    const result = {};

    for (const championship of championships) {
      result[championship.id] = tournaments.filter((tournament) => {
        const championshipIds = tournament.championship_ids?.length
          ? tournament.championship_ids
          : tournament.championship_id
            ? [tournament.championship_id]
            : [];

        return championshipIds.includes(championship.id);
      });
    }

    return result;
  }, [championships, tournaments]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (championship) => {
    setEditingId(championship.id);
    setForm({
      name: championship.name || "",
      state: championship.state || "draft",
      clan_ranking_mode:
        championship.clan_ranking_mode || "placement",
      placement_points:
        championship.placement_points || {},
      game_win_bonus:
        championship.game_win_bonus ?? 0,
      game_top3_bonus:
        championship.game_top3_bonus ?? 0,
      tournament_win_bonus:
        championship.tournament_win_bonus ?? 0,
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormOpen(false);
  };

  const deleteChampionship = async (championship) => {
    const linkedCount =
      tournamentsByChampionship[championship.id]?.length || 0;

    const confirmed = window.confirm(
      `Eliminare definitivamente il Campionato "${championship.name}"?\n\n` +
        `Il Campionato verrà eliminato, ma i tornei, le squadre, ` +
        `i player, le lobby, le partite, i risultati e tutte le statistiche ` +
        `rimarranno intatti.\n\n` +
        `I ${linkedCount} torneo/i associati verranno semplicemente scollegati.`
    );

    if (!confirmed) {
      return;
    }

    try {
      await api.delete(`/championships/${championship.id}`);

      setChampionships((current) =>
        current.filter((item) => item.id !== championship.id)
      );

      setTournaments((current) =>
        current.map((tournament) => {
          const championshipIds = tournament.championship_ids?.length
            ? tournament.championship_ids
            : tournament.championship_id
              ? [tournament.championship_id]
              : [];

          if (!championshipIds.includes(championship.id)) {
            return tournament;
          }

          const nextIds = championshipIds.filter(
            (id) => id !== championship.id
          );

          return {
            ...tournament,
            championship_ids: nextIds,
            championship_id: nextIds[0] || null,
          };
        })
      );

      setRankings((current) => {
        const next = { ...current };
        delete next[championship.id];
        return next;
      });

      setHistories((current) => {
        const next = { ...current };
        delete next[championship.id];
        return next;
      });

      if (expandedId === championship.id) {
        setExpandedId(null);
      }

      toast.success(`Campionato "${championship.name}" eliminato`);
    } catch (error) {
      toast.error(formatError(error.response?.data?.detail));
    }
  };

  const setPlacementPoint = (position, value) => {
    const key = String(position);
    const next = {
      ...form.placement_points,
    };

    if (value === "") {
      delete next[key];
    } else {
      next[key] = Number(value);
    }

    setForm({
      ...form,
      placement_points: next,
    });
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Nome Campionato obbligatorio");
      return;
    }

    const payload = {
      name: form.name.trim(),
      state: form.state,
      clan_ranking_mode: form.clan_ranking_mode,
      placement_points: form.placement_points,
      game_win_bonus: Number(form.game_win_bonus) || 0,
      game_top3_bonus: Number(form.game_top3_bonus) || 0,
      tournament_win_bonus:
        Number(form.tournament_win_bonus) || 0,
    };

    setSaving(true);
    try {
      if (editingId) {
        await api.put(
          `/championships/${editingId}`,
          payload
        );
        toast.success("Campionato aggiornato");
      } else {
        await api.post("/championships", payload);
        toast.success("Campionato creato");
      }

      closeForm();
      await load();
    } catch (error) {
      toast.error(formatError(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const persistedState = editingId
    ? championships.find(
        (championship) => championship.id === editingId
      )?.state || form.state
    : "draft";

  const availableStates = editingId
    ? NEXT_STATES[persistedState] || [persistedState]
    : ["draft"];

  return (
    <div>
      <PageTitle
        title="Campionati"
        subtitle="Aggregazione dei tornei per Clan e MVP"
        action={(
          <button
            data-testid="new-championship-btn"
            onClick={openCreate}
            className="btn-gold clip-beveled px-6 py-2.5 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Nuovo Campionato
          </button>
        )}
      />

      {formOpen && (
        <Panel
          gold
          className="p-6 mb-6 boot-in"
          testid="championship-form"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="heading text-2xl gold-text">
              {editingId
                ? "Modifica Campionato"
                : "Crea Campionato"}
            </h2>
            <button
              onClick={closeForm}
              className="text-[var(--silver)] hover:text-white"
              aria-label="Chiudi"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">
                Nome Campionato *
              </label>
              <input
                data-testid="championship-name"
                value={form.name}
                onChange={(event) => setForm({
                  ...form,
                  name: event.target.value,
                })}
                disabled={form.state === "archived"}
                className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white focus:border-[var(--gold)] focus:outline-none disabled:opacity-50"
              />
            </div>

            <div>
              <label className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">
                Stato
              </label>
              <select
                data-testid="championship-state"
                value={form.state}
                onChange={(event) => setForm({
                  ...form,
                  state: event.target.value,
                })}
                disabled={form.state === "archived"}
                className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white font-rajdhani focus:border-[var(--gold)] focus:outline-none disabled:opacity-50"
              >
                {availableStates.map((state) => (
                  <option key={state} value={state}>
                    {STATE_LABELS[state]}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">
                Modalità Classifica Clan
              </label>
              <select
                data-testid="championship-mode"
                value={form.clan_ranking_mode}
                onChange={(event) => setForm({
                  ...form,
                  clan_ranking_mode: event.target.value,
                })}
                disabled={form.state === "archived"}
                className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white font-rajdhani focus:border-[var(--gold)] focus:outline-none disabled:opacity-50"
              >
                <option value="placement">
                  Classifica piazzamenti
                </option>
                <option value="balanced">
                  Classifica bilanciata
                </option>
              </select>
            </div>
          </div>

          {form.clan_ranking_mode === "placement" ? (
            <div className="mt-5">
              <StarDivider
                label="Punti per piazzamento"
                className="mb-3"
              />
              <div className="grid grid-cols-2 sm:grid-cols-5 lg:grid-cols-10 gap-2">
                {Array.from(
                  { length: 10 },
                  (_, index) => index + 1
                ).map((position) => (
                  <div key={position}>
                    <label className="font-oswald text-xs text-[var(--gold)]">
                      {position}°
                    </label>
                    <input
                      type="number"
                      min="0"
                      data-testid={`championship-points-${position}`}
                      value={
                        form.placement_points[
                          String(position)
                        ] ?? ""
                      }
                      onChange={(event) =>
                        setPlacementPoint(
                          position,
                          event.target.value
                        )
                      }
                      disabled={form.state === "archived"}
                      className="w-full bg-[#0a0a0a] border border-[var(--steel)]/50 px-2 py-1.5 text-center text-white focus:border-[var(--gold)] focus:outline-none disabled:opacity-50"
                    />
                  </div>
                ))}
              </div>
              <p className="font-rajdhani text-xs text-[var(--silver)] mt-2">
                I punti vengono sommati per Clan attraverso team,
                lobby e tornei associati.
              </p>
            </div>
          ) : (
            <div className="mt-5 border border-[#22d3ee]/30 bg-[#22d3ee]/5 p-4 clip-beveled-sm">
              <p className="font-rajdhani text-sm text-[#22d3ee]">
                Per ogni singolo torneo: somma dei punteggi dei
                team del Clan divisa per il numero di team
                partecipanti.
              </p>
              <p className="font-rajdhani text-xs text-[var(--silver)] mt-1">
                Nel Campionato vengono sommate le medie ottenute
                dal Clan in ciascun torneo associato.
              </p>
            </div>
          )}

          <div className="mt-5">
            <StarDivider
              label="Punti bonus Campionato"
              className="mb-3"
            />

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">
                  Vittoria singolo game (1° posto)
                </label>
                <input
                  type="number"
                  min="0"
                  data-testid="championship-game-win-bonus"
                  value={form.game_win_bonus}
                  onChange={(event) => setForm({
                    ...form,
                    game_win_bonus: event.target.value,
                  })}
                  disabled={form.state === "archived"}
                  className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-center text-white focus:border-[var(--gold)] focus:outline-none disabled:opacity-50"
                />
              </div>

              <div>
                <label className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">
                  2° / 3° posto singolo game
                </label>
                <input
                  type="number"
                  min="0"
                  data-testid="championship-game-top3-bonus"
                  value={form.game_top3_bonus}
                  onChange={(event) => setForm({
                    ...form,
                    game_top3_bonus: event.target.value,
                  })}
                  disabled={form.state === "archived"}
                  className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-center text-white focus:border-[var(--gold)] focus:outline-none disabled:opacity-50"
                />
              </div>

              <div>
                <label className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">
                  Vittoria classifica generale torneo
                </label>
                <input
                  type="number"
                  min="0"
                  data-testid="championship-tournament-win-bonus"
                  value={form.tournament_win_bonus}
                  onChange={(event) => setForm({
                    ...form,
                    tournament_win_bonus: event.target.value,
                  })}
                  disabled={form.state === "archived"}
                  className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-center text-white focus:border-[var(--gold)] focus:outline-none disabled:opacity-50"
                />
              </div>
            </div>

            <div className="mt-3 border border-[var(--gold)]/20 bg-[var(--gold)]/5 p-3 clip-beveled-sm">
              <p className="font-rajdhani text-xs text-[var(--silver)]">
                Il 1° posto di ogni singolo game riceve esclusivamente
                il bonus vittoria game. Il 2° e il 3° posto ricevono
                il relativo bonus dedicato. Il bonus vittoria torneo
                viene assegnato esclusivamente al 1° classificato
                della Classifica Generale del torneo.
              </p>
              <p className="font-rajdhani text-xs text-[var(--silver)] mt-1">
                Nella Classifica Bilanciata i bonus vengono sommati
                ai punteggi dei singoli team prima di calcolare la
                media del Clan.
              </p>
            </div>
          </div>

          <div className="flex gap-3 mt-5">
            <button
              data-testid="save-championship-btn"
              onClick={save}
              disabled={saving || persistedState === "archived"}
              className="btn-gold clip-beveled px-6 py-2 disabled:opacity-50"
            >
              {saving ? "Salvataggio…" : "Salva"}
            </button>
            <button
              onClick={closeForm}
              className="btn-outline-gold clip-beveled px-6 py-2"
            >
              Annulla
            </button>
          </div>
        </Panel>
      )}

      <div className="grid xl:grid-cols-2 gap-5">
        {[...activeChampionships, ...completedChampionships].map((championship) => {
          const linkedTournaments =
            tournamentsByChampionship[championship.id] || [];
          const points = Object.entries(
            championship.placement_points || {}
          ).sort(
            ([left], [right]) =>
              Number(left) - Number(right)
          );

          const isCompleted = ["completed", "archived"].includes(championship.state);
          const isExpanded = expandedCompletedIds.includes(championship.id);

          return (
            <Fragment key={championship.id}>
              {isCompleted && championship.id === completedChampionships[0]?.id && (
                <div className="xl:col-span-2 mt-6">
                  <StarDivider label={`Campionati conclusi (${completedChampionships.length})`} className="mb-4" />
                  <div className="hidden md:grid grid-cols-[minmax(0,2fr)_1fr_1fr_1fr_auto] gap-3 items-center px-4 py-3 bg-[#111] border border-[var(--steel)]/40 font-rajdhani text-xs uppercase tracking-wider text-[var(--gold)]">
                    <span>Campionato</span>
                    <span>Modalità classifica</span>
                    <span>Tornei associati</span>
                    <span>Stato</span>
                    <span>Dettagli</span>
                  </div>
                </div>
              )}
              {isCompleted && (
                <div className="xl:col-span-2 border border-[var(--steel)]/40 bg-[#111]">
                  <div className="grid grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[minmax(0,2fr)_1fr_1fr_1fr_auto] gap-3 items-center p-4 font-rajdhani">
                    <div className="min-w-0">
                      <p className="font-semibold text-white truncate">{championship.name}</p>
                      <p className="text-xs text-[var(--silver)] md:hidden">
                        {MODE_LABELS[championship.clan_ranking_mode] || championship.clan_ranking_mode}
                      </p>
                    </div>
                    <span className="hidden md:block text-xs text-[var(--silver)]">
                      {MODE_LABELS[championship.clan_ranking_mode] || championship.clan_ranking_mode}
                    </span>
                    <span className="hidden md:block text-xs text-[var(--silver)]">
                      {linkedTournaments.length} tornei associati
                    </span>
                    <span className="hidden md:block text-xs text-[var(--silver)]">
                      {STATE_LABELS[championship.state]}
                    </span>
                    <button
                      type="button"
                      aria-expanded={isExpanded}
                      onClick={() => setExpandedCompletedIds((prev) =>
                        prev.includes(championship.id)
                          ? prev.filter((id) => id !== championship.id)
                          : [...prev, championship.id]
                      )}
                      className="btn-outline-gold clip-beveled-sm px-3 py-1.5 text-xs flex items-center gap-1"
                    >
                      {isExpanded ? "Chiudi" : "Espandi"}
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}
              {(!isCompleted || isExpanded) && (
            <Panel
              className={isCompleted ? "p-5 xl:col-span-2" : "p-5"}
              testid={`championship-${championship.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <Medal className="w-7 h-7 text-[var(--gold)] mt-1" />
                  <div>
                    <h3 className="heading text-3xl gold-text">
                      {championship.name}
                    </h3>
                    <p className="font-rajdhani text-xs uppercase tracking-widest text-[var(--silver)]">
                      {MODE_LABELS[
                        championship.clan_ranking_mode
                      ] || championship.clan_ranking_mode}
                    </p>
                  </div>
                </div>
                <span className="px-3 py-1 bg-[#1a1a1a] border border-[var(--gold)]/40 text-[var(--gold)] font-rajdhani text-xs uppercase clip-beveled-sm">
                  {STATE_LABELS[championship.state]}
                </span>
              </div>

              <div className="mt-4">
                <p className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)] mb-2">
                  Tornei associati
                </p>
                <div className="flex flex-wrap gap-2">
                  {linkedTournaments.map((tournament) => (
                    <span
                      key={tournament.id}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-[#0a0a0a] border border-[var(--steel)]/40 text-white font-rajdhani text-xs clip-beveled-sm"
                    >
                      <Trophy className="w-3 h-3 text-[var(--gold)]" />
                      {tournament.name}
                    </span>
                  ))}
                  {linkedTournaments.length === 0 && (
                    <span className="font-rajdhani text-xs text-[var(--silver)]">
                      Nessun torneo associato
                    </span>
                  )}
                </div>
              </div>

              {championship.clan_ranking_mode === "placement" && (
                <div className="mt-4">
                  <p className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)] mb-2">
                    Tabella punti
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {points.map(([position, value]) => (
                      <span
                        key={position}
                        className="px-2 py-1 bg-[var(--gold)]/10 text-[var(--gold-light)] font-oswald text-xs"
                      >
                        {position}° → {value}
                      </span>
                    ))}
                    {points.length === 0 && (
                      <span className="font-rajdhani text-xs text-[var(--silver)]">
                        Da configurare
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3 mt-5">
                <button
                  data-testid={`championship-rankings-${championship.id}`}
                  onClick={() => toggleRankings(championship.id)}
                  className="btn-outline-gold clip-beveled px-4 py-2 flex items-center gap-2 font-rajdhani text-sm"
                >
                  {expandedId === championship.id ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                  {expandedId === championship.id
                    ? "Nascondi classifiche"
                    : "Mostra classifiche"}
                </button>

                <div className="flex items-center gap-4">
                  <button
                    data-testid={`edit-championship-${championship.id}`}
                    onClick={() => openEdit(championship)}
                    className="text-[var(--gold)] hover:text-[var(--gold-light)] flex items-center gap-1 font-rajdhani text-sm"
                  >
                    <Pencil className="w-4 h-4" />
                    Gestisci
                  </button>

                  <button
                    data-testid={`delete-championship-${championship.id}`}
                    onClick={() => deleteChampionship(championship)}
                    className="text-[var(--destructive)] hover:text-red-400 flex items-center gap-1 font-rajdhani text-sm"
                  >
                    <X className="w-4 h-4" />
                    Elimina
                  </button>
                </div>
              </div>

              {expandedId === championship.id && (
                <div className="mt-5 space-y-5">
                  {rankingLoading[championship.id] ? (
                    <div className="border border-[var(--steel)]/30 p-6 text-center clip-beveled-sm">
                      <p className="font-rajdhani text-sm text-[var(--silver)]">
                        Caricamento classifiche…
                      </p>
                    </div>
                  ) : rankings[championship.id] ? (
                    <>
                      <div className="grid sm:grid-cols-3 gap-3">
                        <div className="border border-[var(--steel)]/30 p-3 clip-beveled-sm">
                          <p className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">
                            Tornei conteggiati
                          </p>
                          <p className="font-oswald text-2xl text-white">
                            {rankings[championship.id].clans.tournaments}
                          </p>
                        </div>

                        <div className="border border-[var(--steel)]/30 p-3 clip-beveled-sm">
                          <p className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">
                            Clan in classifica
                          </p>
                          <p className="font-oswald text-2xl text-white">
                            {rankings[championship.id].clans.ranking.length}
                          </p>
                        </div>

                        <div className="border border-[var(--steel)]/30 p-3 clip-beveled-sm">
                          <p className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">
                            Team senza Clan
                          </p>
                          <p className={`font-oswald text-2xl ${
                            rankings[championship.id].clans.unassigned_teams
                              ? "text-[var(--destructive)]"
                              : "text-white"
                          }`}>
                            {rankings[championship.id].clans.unassigned_teams}
                          </p>
                        </div>
                      </div>

                      <div className="border border-[var(--gold)]/30 overflow-x-auto clip-beveled-sm">
                        <div className="bg-[#0a0a0a] px-4 py-3 border-b border-[var(--gold)]/30">
                          <p className="heading text-xl text-[var(--gold-light)]">
                            Classifica Clan
                          </p>
                          <p className="font-rajdhani text-xs text-[var(--silver)]">
                            {championship.clan_ranking_mode === "balanced"
                              ? "Punteggio Campionato = somma delle medie Clan ottenute in ciascun torneo."
                              : "Punti assegnati in base ai piazzamenti finali dei team nelle lobby."}
                          </p>
                        </div>

                        <table className="w-full min-w-[720px]">
                          <thead>
                            <tr className="bg-[#0a0a0a] border-b border-[var(--gold)]/40 font-teko text-lg text-[var(--gold-light)] tracking-wide">
                              <th className="text-left px-4 py-3 w-16">Pos</th>
                              <th className="text-left px-4 py-3">Clan</th>
                              <th className="text-right px-4 py-3">Tornei</th>
                              <th className="text-right px-4 py-3">Team</th>
                              <th className="text-right px-4 py-3">Vittorie</th>
                              <th className="text-right px-4 py-3">Miglior piazz.</th>
                              <th className="text-right px-4 py-3">Punti</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rankings[championship.id].clans.ranking.map((row) => (
                              <Fragment key={row.clan_id}>
                                <tr
                                className={`border-b border-[var(--steel)]/20 hover:bg-[#1a1a1a] font-rajdhani ${
                                  row.rank <= 3
                                    ? "bg-[#1a1a1a] border-l-4 border-[var(--gold)]"
                                    : ""
                                }`}
                                data-testid={`championship-clan-${championship.id}-${row.rank}`}
                              >
                                <td className="px-4 py-3">
                                  <RankIcon rank={row.rank} />
                                </td>
                                <td className="px-4 py-3 text-white">
                                  {row.name}
                                  {row.tag && (
                                    <span className="text-[var(--gold)] text-xs ml-1">
                                      ({row.tag})
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-right font-oswald text-white">
                                  {row.tournaments}
                                </td>
                                <td className="px-4 py-3 text-right font-oswald text-white">
                                  {row.teams}
                                </td>
                                <td className="px-4 py-3 text-right font-oswald text-white">
                                  {row.wins}
                                </td>
                                <td className="px-4 py-3 text-right font-oswald text-white">
                                  {row.best_placement ?? "—"}
                                </td>
                                <td className="px-4 py-3 text-right font-oswald font-bold text-2xl text-[var(--gold)]">
                                  {row.points}
                                </td>
                              </tr>

                              {championship.clan_ranking_mode === "balanced" &&
                                row.balanced_details?.length > 0 && (
                                  <tr className="border-b border-[var(--steel)]/20 bg-[#0d0d0d]">
                                    <td colSpan="7" className="px-4 py-3">
                                      <div className="font-rajdhani">
                                        <p className="text-[10px] uppercase tracking-widest text-[#22d3ee] mb-2">
                                          Dettaglio medie per torneo
                                        </p>

                                        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-2">
                                          {row.balanced_details.map((detail) => (
                                            <div
                                              key={detail.tournament_id}
                                              className="border border-[#22d3ee]/20 bg-[#22d3ee]/5 px-3 py-2 clip-beveled-sm"
                                            >
                                              <p className="text-white font-semibold">
                                                {detail.tournament_name || "Torneo"}
                                              </p>
                                              <p className="text-xs text-[var(--silver)] mt-1">
                                                {detail.total_points} punti / {detail.team_count} team
                                              </p>
                                              <p className="text-sm text-[#22d3ee] font-oswald mt-1">
                                                Media: {detail.average_points}
                                              </p>
                                              <p className="text-xs text-[var(--silver)] mt-1">
                                                Vittorie: {detail.wins}
                                                {" · "}
                                                Miglior piazzamento: {detail.best_placement ?? "—"}
                                              </p>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </Fragment>
                            ))}
                            {rankings[championship.id].clans.ranking.length === 0 && (
                              <tr>
                                <td
                                  colSpan="7"
                                  className="text-center py-8 text-[var(--silver)] font-rajdhani"
                                >
                                  Nessun dato Clan disponibile.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div className="border border-[#a78bfa]/30 overflow-x-auto clip-beveled-sm">
                        <div className="bg-[#0a0a0a] px-4 py-3 border-b border-[#a78bfa]/30 flex items-center gap-2">
                          <Star className="w-5 h-5 text-[#a78bfa]" />
                          <div>
                            <p className="heading text-xl text-[#a78bfa]">
                              Classifica MVP
                            </p>
                            <p className="font-rajdhani text-xs text-[var(--silver)]">
                              Kill sommate attraverso i tornei associati; punti MVP come primo spareggio.
                            </p>
                          </div>
                        </div>

                        <table className="w-full min-w-[760px]">
                          <thead>
                            <tr className="bg-[#0a0a0a] border-b border-[#a78bfa]/40 font-teko text-lg text-[#a78bfa] tracking-wide">
                              <th className="text-left px-4 py-3 w-16">Pos</th>
                              <th className="text-left px-4 py-3">Giocatore</th>
                              <th className="text-left px-4 py-3">Activision ID</th>
                              <th className="text-left px-4 py-3">Team</th>
                              <th className="text-right px-4 py-3">Tornei</th>
                              <th className="text-right px-4 py-3">Match</th>
                              <th className="text-right px-4 py-3">Kill</th>
                              <th className="text-right px-4 py-3">Punti MVP</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rankings[championship.id].mvp.ranking.map((row) => (
                              <tr
                                key={row.player_id}
                                className={`border-b border-[var(--steel)]/20 hover:bg-[#1a1a1a] font-rajdhani ${
                                  row.rank <= 3
                                    ? "bg-[#1a1a1a] border-l-4 border-[#a78bfa]"
                                    : ""
                                }`}
                                data-testid={`championship-mvp-${championship.id}-${row.rank}`}
                              >
                                <td className="px-4 py-3">
                                  <RankIcon rank={row.rank} />
                                </td>
                                <td className="px-4 py-3 text-white">
                                  {row.name}
                                </td>
                                <td className="px-4 py-3 text-[var(--silver)]">
                                  {row.activision_id || "—"}
                                </td>
                                <td className="px-4 py-3 text-[var(--silver)]">
                                  {row.team_names?.length
                                    ? row.team_names.join(", ")
                                    : "—"}
                                </td>
                                <td className="px-4 py-3 text-right font-oswald text-white">
                                  {row.tournaments}
                                </td>
                                <td className="px-4 py-3 text-right font-oswald text-white">
                                  {row.matches}
                                </td>
                                <td className="px-4 py-3 text-right font-oswald text-white">
                                  {row.kills}
                                </td>
                                <td className="px-4 py-3 text-right font-oswald font-bold text-2xl text-[#a78bfa]">
                                  {row.points}
                                </td>
                              </tr>
                            ))}
                            {rankings[championship.id].mvp.ranking.length === 0 && (
                              <tr>
                                <td
                                  colSpan="8"
                                  className="text-center py-8 text-[var(--silver)] font-rajdhani"
                                >
                                  Nessun dato MVP disponibile.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() => loadChampionshipRankings(championship.id)}
                            disabled={rankingLoading[championship.id]}
                            className="btn-outline-gold clip-beveled px-4 py-2 flex items-center gap-2 font-rajdhani text-sm disabled:opacity-50"
                            data-testid={`refresh-championship-${championship.id}`}
                          >
                            <RefreshCw
                              className={`w-4 h-4 ${
                                rankingLoading[championship.id] ? "animate-spin" : ""
                              }`}
                            />
                            Aggiorna dati
                          </button>
                        </div>

                        <div
                          className="border border-[var(--gold)]/30 clip-beveled-sm overflow-hidden"
                          data-testid={`championship-adjustments-${championship.id}`}
                        >
                          <div className="bg-[#0a0a0a] px-4 py-3 border-b border-[var(--gold)]/30">
                            <p className="heading text-xl text-[var(--gold-light)]">
                              Adjustment amministrativi
                            </p>
                            <p className="font-rajdhani text-xs text-[var(--silver)]">
                              Correzioni manuali dei soli punti Clan del Campionato.
                              I punteggi del torneo, MVP, risultati e statistiche non vengono modificati.
                            </p>
                          </div>

                          <div className="p-4 space-y-4">
                            {(() => {
                              const adjustmentForm =
                                getAdjustmentForm(championship.id);

                              const linkedTournaments =
                                tournamentsByChampionship[championship.id] || [];

                              const clanOptions =
                                adjustmentClanOptions(
                                  championship.id,
                                  adjustmentForm.tournament_id
                                );

                              const adjustmentTeamsCacheKey =
                                adjustmentForm.tournament_id
                                  ? adjustmentTeamsKey(
                                      championship.id,
                                      adjustmentForm.tournament_id
                                    )
                                  : "";

                              const clansLoading =
                                adjustmentTeamsCacheKey
                                  ? Boolean(
                                      adjustmentTeamsLoading[
                                        adjustmentTeamsCacheKey
                                      ]
                                    )
                                  : false;

                              const adjustmentRows =
                                championshipAdjustments[championship.id] || [];

                              return (
                                <>
                                  <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3">
                                    <label className="space-y-1">
                                      <span className="font-rajdhani text-xs uppercase tracking-widest text-[var(--silver)]">
                                        Torneo
                                      </span>
                                      <select
                                        value={adjustmentForm.tournament_id}
                                        onChange={(event) => {
                                          const tournamentId =
                                            event.target.value;

                                          updateAdjustmentForm(
                                            championship.id,
                                            {
                                              tournament_id: tournamentId,
                                              clan_id: "",
                                            }
                                          );

                                          if (tournamentId) {
                                            loadAdjustmentTournamentTeams(
                                              championship.id,
                                              tournamentId
                                            );
                                          }
                                        }}
                                        className="input-dark w-full"
                                        data-testid={`championship-adjustment-tournament-${championship.id}`}
                                      >
                                        <option value="">
                                          Seleziona torneo
                                        </option>
                                        {linkedTournaments.map((tournament) => (
                                          <option
                                            key={tournament.id}
                                            value={tournament.id}
                                          >
                                            {tournament.name}
                                          </option>
                                        ))}
                                      </select>
                                    </label>

                                    <label className="space-y-1">
                                      <span className="font-rajdhani text-xs uppercase tracking-widest text-[var(--silver)]">
                                        Clan
                                      </span>
                                      <select
                                        value={adjustmentForm.clan_id}
                                        onChange={(event) =>
                                          updateAdjustmentForm(
                                            championship.id,
                                            { clan_id: event.target.value }
                                          )
                                        }
                                        disabled={
                                          !adjustmentForm.tournament_id ||
                                          clansLoading
                                        }
                                        className="input-dark w-full disabled:opacity-50"
                                        data-testid={`championship-adjustment-clan-${championship.id}`}
                                      >
                                        <option value="">
                                          Seleziona Clan
                                        </option>
                                        {clanOptions.map((clan) => (
                                          <option
                                            key={clan.id}
                                            value={clan.id}
                                          >
                                            {clan.tag
                                              ? `[${clan.tag}] ${clan.name}`
                                              : clan.name}
                                          </option>
                                        ))}
                                      </select>
                                    </label>

                                    <label className="space-y-1">
                                      <span className="font-rajdhani text-xs uppercase tracking-widest text-[var(--silver)]">
                                        +/- punti
                                      </span>
                                      <input
                                        type="number"
                                        step="any"
                                        value={adjustmentForm.value}
                                        onChange={(event) =>
                                          updateAdjustmentForm(
                                            championship.id,
                                            { value: event.target.value }
                                          )
                                        }
                                        placeholder="Es. -5 oppure 3"
                                        className="input-dark w-full"
                                        data-testid={`championship-adjustment-value-${championship.id}`}
                                      />
                                    </label>

                                    <label className="space-y-1">
                                      <span className="font-rajdhani text-xs uppercase tracking-widest text-[var(--silver)]">
                                        Motivazione
                                      </span>
                                      <input
                                        type="text"
                                        value={adjustmentForm.reason}
                                        onChange={(event) =>
                                          updateAdjustmentForm(
                                            championship.id,
                                            { reason: event.target.value }
                                          )
                                        }
                                        placeholder="Motivazione obbligatoria"
                                        className="input-dark w-full"
                                        data-testid={`championship-adjustment-reason-${championship.id}`}
                                      />
                                    </label>
                                  </div>

                                  {adjustmentForm.tournament_id &&
                                    clansLoading && (
                                      <p className="font-rajdhani text-xs text-[var(--silver)]">
                                        Caricamento Clan partecipanti…
                                      </p>
                                    )}

                                  {adjustmentForm.tournament_id &&
                                    !clansLoading &&
                                    clanOptions.length === 0 && (
                                      <p className="font-rajdhani text-xs text-[var(--silver)]">
                                        Nessun Clan partecipante disponibile per il torneo selezionato.
                                      </p>
                                    )}

                                  <div className="flex justify-end">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        createChampionshipAdjustment(
                                          championship.id
                                        )
                                      }
                                      disabled={
                                        adjustmentSaving[championship.id]
                                      }
                                      className="btn-gold clip-beveled px-4 py-2 font-rajdhani text-sm disabled:opacity-50"
                                      data-testid={`create-championship-adjustment-${championship.id}`}
                                    >
                                      {adjustmentSaving[championship.id]
                                        ? "Salvataggio…"
                                        : "Aggiungi adjustment"}
                                    </button>
                                  </div>

                                  <div className="border border-[var(--steel)]/30 overflow-x-auto clip-beveled-sm">
                                    <table className="w-full min-w-[1050px]">
                                      <thead>
                                        <tr className="bg-[#0a0a0a] border-b border-[var(--gold)]/30 font-teko text-lg text-[var(--gold-light)] tracking-wide">
                                          <th className="text-left px-4 py-3">
                                            Data
                                          </th>
                                          <th className="text-left px-4 py-3">
                                            Torneo
                                          </th>
                                          <th className="text-left px-4 py-3">
                                            Clan
                                          </th>
                                          <th className="text-right px-4 py-3">
                                            +/- punti
                                          </th>
                                          <th className="text-left px-4 py-3">
                                            Motivazione
                                          </th>
                                          <th className="text-left px-4 py-3">
                                            Admin
                                          </th>
                                          <th className="text-right px-4 py-3">
                                            Azione
                                          </th>
                                        </tr>
                                      </thead>

                                      <tbody>
                                        {adjustmentRows.map((adjustment) => (
                                          <tr
                                            key={adjustment.id}
                                            className="border-b border-[var(--steel)]/20 hover:bg-[#1a1a1a] font-rajdhani"
                                          >
                                            <td className="px-4 py-3 text-[var(--silver)] whitespace-nowrap">
                                              {adjustment.created_at
                                                ? new Date(
                                                    adjustment.created_at
                                                  ).toLocaleString("it-IT")
                                                : "—"}
                                            </td>

                                            <td className="px-4 py-3 text-white">
                                              {adjustment.tournament_name ||
                                                adjustment.tournament_id ||
                                                "—"}
                                            </td>

                                            <td className="px-4 py-3 text-white">
                                              {adjustment.clan_tag
                                                ? `[${adjustment.clan_tag}] `
                                                : ""}
                                              {adjustment.clan_name ||
                                                adjustment.clan_id ||
                                                "—"}
                                            </td>

                                            <td
                                              className={`px-4 py-3 text-right font-oswald ${
                                                Number(adjustment.value) > 0
                                                  ? "text-green-400"
                                                  : "text-[var(--destructive)]"
                                              }`}
                                            >
                                              {Number(adjustment.value) > 0
                                                ? "+"
                                                : ""}
                                              {adjustment.value}
                                            </td>

                                            <td className="px-4 py-3 text-[var(--silver)]">
                                              {adjustment.reason || "—"}
                                            </td>

                                            <td className="px-4 py-3 text-[var(--silver)]">
                                              {adjustment.admin || "—"}
                                            </td>

                                            <td className="px-4 py-3 text-right">
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  deleteChampionshipAdjustment(
                                                    championship.id,
                                                    adjustment
                                                  )
                                                }
                                                disabled={
                                                  adjustmentSaving[
                                                    championship.id
                                                  ]
                                                }
                                                className="text-[var(--destructive)] hover:text-red-400 font-rajdhani text-sm disabled:opacity-50"
                                                data-testid={`delete-championship-adjustment-${adjustment.id}`}
                                              >
                                                Elimina
                                              </button>
                                            </td>
                                          </tr>
                                        ))}

                                        {adjustmentRows.length === 0 && (
                                          <tr>
                                            <td
                                              colSpan="7"
                                              className="text-center py-8 text-[var(--silver)] font-rajdhani"
                                            >
                                              Nessun adjustment amministrativo registrato.
                                            </td>
                                          </tr>
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        </div>

                        <div className="border border-[var(--steel)]/30 overflow-x-auto clip-beveled-sm">
                          <div className="bg-[#0a0a0a] px-4 py-3 border-b border-[var(--steel)]/30 flex items-center gap-2">
                            <History className="w-5 h-5 text-[var(--gold)]" />
                            <div>
                              <p className="heading text-xl text-[var(--gold-light)]">
                                Storico Campionato
                              </p>
                              <p className="font-rajdhani text-xs text-[var(--silver)]">
                                Modifiche del Campionato e associazioni/rimozioni dei tornei.
                              </p>
                            </div>
                          </div>

                          <table className="w-full min-w-[900px]">
                            <thead>
                              <tr className="bg-[#0a0a0a] border-b border-[var(--gold)]/30 font-teko text-lg text-[var(--gold-light)] tracking-wide">
                                <th className="text-left px-4 py-3">Data</th>
                                <th className="text-left px-4 py-3">Utente</th>
                                <th className="text-left px-4 py-3">Evento</th>
                                <th className="text-left px-4 py-3">Riferimento</th>
                                <th className="text-left px-4 py-3">Variazione</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(histories[championship.id] || []).map((row, index) => (
                                <tr
                                  key={`${row.timestamp || "history"}-${index}`}
                                  className="border-b border-[var(--steel)]/20 hover:bg-[#1a1a1a] font-rajdhani"
                                >
                                  <td className="px-4 py-3 text-[var(--silver)] whitespace-nowrap">
                                    {row.timestamp
                                      ? new Date(row.timestamp).toLocaleString("it-IT")
                                      : "—"}
                                  </td>
                                  <td className="px-4 py-3 text-white">
                                    {row.user || "—"}
                                  </td>
                                  <td className="px-4 py-3 text-white">
                                    {row.action || "—"}
                                  </td>
                                  <td className="px-4 py-3 text-[var(--silver)]">
                                    {row.tournament_name ||
                                      (row.entity === "championship"
                                        ? championship.name
                                        : row.entity_id) ||
                                      "—"}
                                  </td>
                                  <td className="px-4 py-3 text-[var(--silver)]">
                                    {row.field ? (
                                      <>
                                        <span className="text-white">{row.field}</span>
                                        {" · "}
                                        {row.old_value !== undefined &&
                                        row.old_value !== null &&
                                        row.old_value !== ""
                                          ? String(row.old_value)
                                          : "—"}
                                        {" → "}
                                        {row.new_value !== undefined &&
                                        row.new_value !== null &&
                                        row.new_value !== ""
                                          ? String(row.new_value)
                                          : "—"}
                                      </>
                                    ) : (
                                      "—"
                                    )}
                                  </td>
                                </tr>
                              ))}

                              {(histories[championship.id] || []).length === 0 && (
                                <tr>
                                  <td
                                    colSpan="5"
                                    className="text-center py-8 text-[var(--silver)] font-rajdhani"
                                  >
                                    Nessun evento storico disponibile.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                    </>
                  ) : (
                    <div className="border border-[var(--steel)]/30 p-6 text-center clip-beveled-sm">
                      <p className="font-rajdhani text-sm text-[var(--silver)]">
                        Classifiche non disponibili.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </Panel>
              )}
            </Fragment>
          );
        })}

        {!loading && championships.length === 0 && (
          <Panel className="p-10 text-center xl:col-span-2">
            <Medal className="w-10 h-10 text-[var(--gold)] mx-auto mb-3" />
            <p className="font-rajdhani text-[var(--silver)]">
              Nessun Campionato configurato.
            </p>
          </Panel>
        )}
      </div>
    </div>
  );
}
